import { ListItem, Popover, TextField, Typography } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';

interface AutocompleteTextFieldProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  name?: string;
  className?: string;
  sx?: any;
  InputProps?: any;
  [key: string]: any;
}

const AutocompleteTextField: React.FC<AutocompleteTextFieldProps> = ({
  value,
  onChange,
  placeholder,
  name,
  className,
  sx,
  InputProps,
  ...props
}) => {
  const [showHints, setShowHints] = useState<boolean>(false);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  
  const { schema } = useFlowsStore(state => state);
  const { user } = useAuthStore(state => state);
  const inputRef = useRef<HTMLInputElement>(null);

  const extractSuggestions = useCallback(
    (schema: any, path: string[] = []): string[] => {
      let suggestions: string[] = [];

      for (const key in schema) {
        const newPath: any = [...path, key];
        suggestions.push(newPath.join('.'));

        if (typeof schema[key] === 'object') {
          suggestions = suggestions.concat(
            extractSuggestions(schema[key], newPath),
          );
        }
      }

      return suggestions;
    },
    [],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      
      setCursorPosition(cursorPos);
      onChange(e);

      if (!inputValue || typeof inputValue !== 'string') {
        setAnchorEl(null);
        setShowHints(false);
        return;
      }

      // Check if user just typed {{
      const lastTwoChars = inputValue.slice(cursorPos - 2, cursorPos);
      if (lastTwoChars === '{{') {
        const suggestions = extractSuggestions(schema);
        setSuggestedTags(suggestions);
        setAnchorEl(e.currentTarget);
        setShowHints(true);
        return;
      }

      // Find all pairs of {{ and }} in the text
      const regex = /{{(.*?)}}/g;
      const matches = inputValue ? [...String(inputValue).matchAll(regex)] : [];

      if (matches.length === 0) {
        setShowHints(false);
        setAnchorEl(null);
        return;
      }

      // Check if cursor is within any of the {{ }} pairs
      let isWithinBrackets = false;
      for (const match of matches) {
        const matchStart = match.index;
        const matchEnd = match.index + match[0].length;

        if (cursorPos >= matchStart && cursorPos <= matchEnd) {
          isWithinBrackets = true;
          const text = match[1].trim();
          const suggestions = extractSuggestions(schema);

          if (text.length > 0) {
            const textFilter = suggestions.filter(
              (suggestion: any) =>
                suggestion.toLowerCase().includes(text.toLowerCase()),
            );
            setSuggestedTags(textFilter);
          } else {
            setSuggestedTags(suggestions);
          }
          setAnchorEl(e.currentTarget);
          setShowHints(true);
          break;
        }
      }

      if (!isWithinBrackets) {
        setShowHints(false);
        setAnchorEl(null);
      }
    },
    [onChange, extractSuggestions, schema],
  );

  const handleSuggestionClick = (suggestion: string) => {
    if (!inputRef.current) return;

    const inputValue = value;
    const cursorPos = cursorPosition;

    // Find the current {{ }} pair that contains the cursor
    const regex = /{{(.*?)}}/g;
    const matches = inputValue ? [...String(inputValue).matchAll(regex)] : [];

    let newValue = inputValue;
    let foundMatch = false;

    for (const match of matches) {
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;

      if (cursorPos >= matchStart && cursorPos <= matchEnd) {
        // Replace the content inside the existing {{ }} pair
        const beforeMatch = inputValue.substring(0, matchStart + 2); // Include {{
        const afterMatch = inputValue.substring(matchEnd - 2); // Include }}
        newValue = `${beforeMatch}${suggestion}${afterMatch}`;
        foundMatch = true;
        break;
      }
    }

    // If no complete match found, check for incomplete {{ at cursor position
    if (!foundMatch) {
      const lastTwoChars = inputValue.slice(cursorPos - 2, cursorPos);
      if (lastTwoChars === '{{') {
        // User just typed {{, insert suggestion and close }}
        const beforeCursor = inputValue.substring(0, cursorPos);
        const afterCursor = inputValue.substring(cursorPos);
        newValue = `${beforeCursor}${suggestion}}}${afterCursor}`;
      } else {
        // Insert complete {{suggestion}} at cursor position
        const beforeCursor = inputValue.substring(0, cursorPos);
        const afterCursor = inputValue.substring(cursorPos);
        newValue = `${beforeCursor}{{${suggestion}}}${afterCursor}`;
      }
    }

    const syntheticEvent = {
      target: {
        name: name || '',
        value: newValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
    setShowHints(false);
    setAnchorEl(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showHints && suggestedTags.length > 0) {
      if (e.key === 'Escape') {
        setShowHints(false);
        setAnchorEl(null);
      }
    }
  };

  return (
    <>
      <TextField
        {...props}
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        name={name}
        className={className}
        sx={sx}
        InputProps={InputProps}
      />
      
      <Popover
        open={showHints}
        anchorEl={anchorEl}
        onClose={() => {
          setShowHints(false);
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxHeight: 200,
            overflow: 'auto',
            backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
          },
        }}
      >
        {suggestedTags.map((suggestion, index) => (
          <ListItem
            key={index}
            onClick={() => handleSuggestionClick(suggestion)}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: user?.theme === 'dark' ? '#475467' : '#EAECF0',
              },
            }}
          >
            <Typography
              sx={{
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                fontSize: '14px',
              }}
            >
              {suggestion}
            </Typography>
          </ListItem>
        ))}
      </Popover>
    </>
  );
};

export default AutocompleteTextField;
